import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import UserAvatar from "@/components/controls/UserAvatar";
import {
  ArrMrr,
  ArrowUpRight,
  Calendar,
  ChevronDown,
  ChevronRight,
  Delete,
  Product,
  Rhombus
} from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import {
  formatCurrency,
  formatDateShort
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { VersionChanges } from "./VersionChanges";
import { versionMarker, versionPeriod, versionTitle } from "./versionLabels";
import { versionTcvDelta } from "./versionDelta";
import { TermsVersionSummary } from "./types";

// The bullet sits 12px down (mt-3) and is 14px tall, so its centre is 19px
// from the entry's top.
const BULLET_CENTER_Y = 19;
// The rail overruns into the space between entries, so the list applies this as
// its own gap rather than a `gap-4` class the overrun would have to mirror.
export const TIMELINE_GAP = 16;

interface Props {
  contractId: string;
  version: TermsVersionSummary;
  // The connector runs the full height of the rail, so the first and last
  // entries are the only ones that stop it — at their own bullet.
  isFirst: boolean;
  isLast: boolean;
  onViewTerms: (version: TermsVersionSummary) => void;
  onDiscard: (version: TermsVersionSummary) => void;
  /** Defaults false so the control never appears before the gate resolves. */
  canDiscard?: boolean;
}

// Deliberately not called a chip. The design system's DESIGN.md reserves "chip" for a filled or
// bordered pill, and `@/components/ui/chip` exports a `Chip`/`ChipProps` pair
// that renders exactly that; this has no fill, border or radius — it is an icon
// beside a figure in the card's subtitle row, which is what its own comment
// below says. (The Figma design does show these as bordered pills; that gap is
// open on the ticket. If it is closed, the shared Chip is the thing to reach
// for rather than restyling this.)
interface SubtitleValueProps {
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  dataTest?: string;
  // `span` where the chip renders inside the disclosure button, whose content
  // model admits phrasing content only.
  as?: "div" | "span";
}

// A subtitle value: a 16px icon and its figure.
const SubtitleValue: React.FC<SubtitleValueProps> = ({
  icon,
  children,
  className,
  dataTest,
  as: Wrapper = "div"
}) => (
  <Wrapper className="flex items-center gap-1 shrink-0" data-test={dataTest}>
    {icon ? (
      <IconContainer size="sm" color="secondary" as={Wrapper}>
        {icon}
      </IconContainer>
    ) : null}
    <Text variant="body" as={Wrapper} className={className}>
      {children}
    </Text>
  </Wrapper>
);

// One saved version. The rail carries when it applied and who saved it, joined
// to its neighbours by an unbroken connector; the card carries what it was
// worth, and what it changed once expanded.
export const TermsHistoryEntry: React.FC<Props> = ({
  contractId,
  version,
  isFirst,
  isLast,
  onViewTerms,
  onDiscard,
  canDiscard = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  // Latched on the first expand and never cleared. The panel's contents stay
  // mounted from then on so collapsing animates, while an entry nobody has
  // opened still issues no changes request.
  const [hasExpanded, setHasExpanded] = useState(false);
  // Contract-currency only, with no fallback to the account-currency twin — see
  // the pair semantics on `TermsVersionFinancials` in types.ts. TCV is one of
  // the two fields where a populated account figure with a null local one is
  // actually reachable, so this matters here rather than being defensive.
  const delta = versionTcvDelta(
    version.totalContractValueLocalChange,
    version.currency
  );
  const marker = versionMarker(version, t);
  const period = versionPeriod(version, t);
  const isInitialTerms = version.versionIndex === 0;
  // `useId` for the same reason the two table titles use it: the id only has to
  // be unique per mount, and this is the convention the repo already follows for
  // aria associations. Deriving it from `versionId` also worked, but left this
  // the only hand-built id of the three in the feature.
  const panelId = React.useId();
  // The list endpoint resolves the name, and the avatar reads only these fields.
  const author = version.createdByName
    ? { displayName: version.createdByName, avatarUrl: "" }
    : undefined;

  return (
    <div
      className="flex gap-4 items-stretch"
      data-test={`terms-version-${version.versionId}`}
    >
      {/* One unbroken rail: the line spans the entry's full height and overruns
          the flex gap into the next entry, so nothing is bare between bullets.
          It starts and ends at a bullet's centre only at the two ends of the
          list. */}
      <div className="relative flex flex-col items-center shrink-0 w-5">
        {isFirst && isLast ? null : (
          <div
            className="absolute left-1/2 -translate-x-1/2 w-px bg-border-border1"
            style={{
              top: isFirst ? BULLET_CENTER_Y : 0,
              bottom: isLast
                ? `calc(100% - ${BULLET_CENTER_Y}px)`
                : -TIMELINE_GAP
            }}
            aria-hidden
          />
        )}
        {/* 14px, in the border colour, on the page ground so the rail passes
            behind it rather than through it. The fill has to be the ground the
            timeline actually sits on — `surface-elevation0`, what the record
            page paints — or it reads as a dark tile behind the marker. */}
        <IconContainer
          as="span"
          size="14"
          color="border-border1"
          nativeStroke
          className="relative mt-3 shrink-0 bg-surface-elevation0 [&_svg]:size-full"
        >
          <Rhombus />
        </IconContainer>
      </div>

      <div className="w-60 shrink-0 flex flex-col gap-1.5 py-2">
        {version.isPending ? (
          <>
            <div className="bg-badges-natural rounded-md px-1.5 py-0.5 w-fit">
              <Text variant="smBody" className="text-natural">
                {t("contracts.termsHistory.upcoming")}
              </Text>
            </div>
            {version.effectiveDate ? (
              <Text variant="smBodySecondary">
                {t("contracts.termsHistory.startsOn", {
                  date: formatDateShort(version.effectiveDate)
                })}
              </Text>
            ) : null}
          </>
        ) : (
          <Text variant="body">{period ?? "—"}</Text>
        )}
        <div className="flex items-center gap-1.5 min-w-0">
          <UserAvatar
            userData={author}
            size="S"
            variant="identity"
            className="shrink-0"
          />
          <Text variant="smBodySecondary">
            {version.createdByName
              ? t("contracts.termsHistory.savedBy", {
                  name: version.createdByName
                })
              : t("contracts.termsHistory.savedByUnknown")}
            {version.createdAt
              ? ` • ${formatDateShort(version.createdAt)}`
              : ""}
          </Text>
        </div>
      </div>

      {/* A plain container, not a control. The disclosure is its own native
          button below, so "View terms" and the expanded panel are its siblings
          rather than its descendants: nesting a button inside a role="button"
          suppresses the inner one for assistive tech and folds every bit of
          descendant text — the whole changes table, once expanded — into the
          outer control's accessible name. */}
      <div className="group flex-1 min-w-0 rounded-xl border border-border-widget-card bg-surface-elevation0 transition-colors hover:bg-surface-hover-soft">
        <div className="flex items-start gap-1 p-2 pr-3">
          {/* Native button, so Enter and Space are the browser's job — no
              key handler, and nothing to stop bubbling from. */}
          <button
            type="button"
            aria-expanded={isExpanded}
            // The panel element is always mounted now — it is what the
            // 0fr/1fr row transition animates — so this id always resolves.
            aria-controls={panelId}
            onClick={() => {
              setIsExpanded((open) => !open);
              setHasExpanded(true);
            }}
            className="flex items-start gap-1 flex-1 min-w-0 text-left cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-selected-tab"
            data-test={`terms-version-toggle-${version.versionId}`}
          >
            {/* Everything below is phrasing content: a <button> admits no <div>
                or <p>, and Text renders a <p> unless told otherwise. */}
            <IconContainer
              size="sm"
              color="primary"
              as="span"
              className="mt-0.5 shrink-0"
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </IconContainer>
            {/* Title and its figures share a column beside the chevron, so the
                figures line up under the title rather than under the chevron. */}
            <span className="flex flex-col gap-1 flex-1 min-w-0">
              <span className="flex items-center gap-2 min-w-0">
                {/* min-w-0 so `truncate` still applies: it is inert on an inline
                    box, and only blockifies here by being a flex item. */}
                <Text variant="label" as="span" className="truncate min-w-0">
                  {versionTitle(version, t)}
                </Text>
                {marker ? (
                  <Text variant="bodySecondary" as="span" className="shrink-0">
                    {marker}
                  </Text>
                ) : null}
              </span>
              <span className="flex flex-wrap items-center gap-5">
                {version.servicesCount != null ? (
                  <SubtitleValue icon={<Product />} as="span">
                    {t("contracts.termsHistory.servicesCount", {
                      count: version.servicesCount
                    })}
                  </SubtitleValue>
                ) : null}
                {/* Suppressed for a pending version: it carries a null
                    `effectiveUntil`, which `versionPeriod` reads as "Ongoing" —
                    for terms that have not started yet, that states the
                    opposite of the rail's own block beside it. */}
                {period && !version.isPending ? (
                  <SubtitleValue
                    icon={<Calendar />}
                    as="span"
                    dataTest={`terms-version-period-${version.versionId}`}
                  >
                    {period}
                  </SubtitleValue>
                ) : null}
                <SubtitleValue icon={<ArrMrr />} as="span">
                  {t("contracts.termsHistory.tcvValue", {
                    value: formatCurrency(
                      version.totalContractValueLocal,
                      version.currency
                    )
                  })}
                </SubtitleValue>
                {/* Suppressed when the version moved nothing, per the design note. */}
                {delta ? (
                  <SubtitleValue
                    as="span"
                    className={cn(
                      delta.isIncrease ? "text-positive" : "text-error"
                    )}
                    dataTest={`terms-version-delta-${version.versionId}`}
                  >
                    {t("contracts.termsHistory.tcvValue", {
                      value: delta.label
                    })}
                  </SubtitleValue>
                ) : null}
              </span>
            </span>
          </button>
          {/* Visible by default, hidden only where hover exists, so a touch
              user never gets an invisible-but-clickable control. `focus-within`
              keeps the row up while a control inside it holds focus. */}
          <div className="flex items-center gap-1 shrink-0 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <Button
              variant="secondary"
              onClick={() => onViewTerms(version)}
              className="shrink-0"
              data-test={`terms-version-view-${version.versionId}`}
            >
              <span className="flex items-center gap-1.5">
                {/* Phrasing content here too: this is a native button, and
                    IconContainer renders a div unless told otherwise. The
                    disclosure button beside it was converted in an earlier pass
                    and this one was missed. */}
                <IconContainer size="sm" color="primary" as="span">
                  <ArrowUpRight />
                </IconContainer>
                {t("contracts.termsHistory.viewTerms")}
              </span>
            </Button>
            {/* `isPending` is false for withdrawn as well as applied. */}
            {version.isPending && version.effectiveDate ? (
              <IconButton
                variant="icon"
                // 32px, matching the View terms button beside it.
                size="lg"
                destructive
                disabled={!canDiscard}
                onClick={() => onDiscard(version)}
                tooltipText={t(
                  canDiscard
                    ? "contracts.termsHistory.discardAmendment.tooltip"
                    : "common.noEditPermission"
                )}
                aria-label={t(
                  "contracts.termsHistory.discardAmendment.tooltip"
                )}
                data-test={`terms-version-discard-${version.versionId}`}
              >
                <Delete />
              </IconButton>
            ) : null}
          </div>
        </div>

        {/* The repo's collapse transition: a 0fr/1fr grid row, animated, with
            the content clipped by the track. The wrapper is always mounted so
            there is an element whose row size can transition; what goes inside
            it is still gated on `hasExpanded`.
            `aria-hidden` while collapsed keeps the zero-height content out of
            the accessibility tree. Safe here because nothing inside is
            focusable — the changes panel is a table of text. */}
        <div
          id={panelId}
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-in-out",
            isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden" aria-hidden={!isExpanded}>
            {hasExpanded ? (
              <div className="border-t border-border-border1">
                {/* The reason the amendment was saved, where one was given.
                    Nothing is rendered for a version saved without one — an
                    empty labelled block states less than no block at all. */}
                {version.amendmentReason ? (
                  <div
                    className="flex flex-col gap-1 px-3 pt-3"
                    data-test={`terms-version-reason-${version.versionId}`}
                  >
                    <Text variant="body">
                      {t("contracts.termsHistory.amendmentReason")}
                    </Text>
                    <Text variant="bodySecondary">
                      {version.amendmentReason}
                    </Text>
                  </div>
                ) : null}
                <VersionChanges
                  contractId={contractId}
                  versionId={version.versionId}
                  isInitialTerms={isInitialTerms}
                  currency={version.currency}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
